// Navigation config for the shell (sidebar, breadcrumb, phone header, bottom tabs, More). Pages are wired in
// App.tsx; adding a page to the menus means one entry here, not a change to Shell.tsx.
import type { NavCounts, Permission } from '../lib/types';
import type { IconName } from './icons';

export interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** Shown only to users with this permission (or any of these). */
  can?: Permission | Permission[];
  /** Sidebar badge from GET /nav/counts, and what the number means (read after it by screen readers). */
  badge?: { key: keyof NavCounts; meaning: string };
  /** Other paths (and their sub-paths) that belong to this entry, e.g. a receipt opened from Movements. */
  match?: string[];
}
export interface NavGroup { group: string; items: NavItem[] }

/** Whether a menu entry shows for a user: no permission needed, or the user has it (any of a list). */
export const allows = (can: (p: Permission) => boolean, need?: Permission | Permission[]) =>
  !need || (Array.isArray(need) ? need.some(can) : can(need));

// PRD "Navigation and routes": Home, then Tracking, Purchasing, Reporting. Settings is the gear in the sidebar footer.
export const NAV: NavGroup[] = [
  { group: '', items: [{ to: '/', label: 'Home', icon: 'home' }] },
  { group: 'Tracking', items: [
    { to: '/stock/move', label: 'Stock movement', icon: 'move', can: 'stock_move' },
    { to: '/stock', label: 'Inventory', icon: 'stock' },
    { to: '/movements', label: 'Movements', icon: 'movements', match: ['/receipts', '/counts'] },
    { to: '/items', label: 'Items', icon: 'items' },
  ] },
  { group: 'Purchasing', items: [
    { to: '/reorder', label: 'Reorder list', icon: 'reorder', badge: { key: 'reorderBelowMin', meaning: 'below min' } },
    { to: '/purchase-requests', label: 'Purchase requests', icon: 'requests', badge: { key: 'prsPending', meaning: 'need your action' } },
    { to: '/purchase-orders', label: 'Purchase orders', icon: 'orders' },
    { to: '/suppliers', label: 'Suppliers', icon: 'suppliers' },
  ] },
  { group: 'Reporting', items: [
    // Audit-only roles still reach the Audit log, which is a tab of Reports.
    { to: '/reports', label: 'Reports', icon: 'reports', can: ['reports', 'audit'] },
    { to: '/periods', label: 'Period close', icon: 'close', can: 'period_close' },
  ] },
];

export type SettingsTab = 'par' | 'kits' | 'locations' | 'users' | 'property';
export interface SettingsEntry { key: SettingsTab; to: string; label: string; needsProperty?: boolean; can?: Permission }

/** The five Settings tabs (PRD F14); chips on phone, also listed on the More screen. */
export const SETTINGS_TABS: SettingsEntry[] = [
  { key: 'par', to: '/settings/par', label: 'Par levels', needsProperty: true },
  { key: 'kits', to: '/settings/kits', label: 'Kits', needsProperty: true },
  { key: 'locations', to: '/settings/locations', label: 'Locations', needsProperty: true },
  { key: 'users', to: '/settings/users', label: 'Users and roles', can: 'users' },
  { key: 'property', to: '/settings/property', label: 'Property', needsProperty: true },
];

/** Pages behind the footer gear, besides the tabs (hub, imports, properties, email log, own account). */
export const SETTINGS_PATHS = ['/settings', '/items/import', '/properties', '/email-log', '/account'];

/** Pages that work without a current property, so a fresh install (or an unassigned user) can still reach them. */
export const NO_PROPERTY_PAGES = ['/settings', '/settings/users', '/account', '/properties', '/email-log', '/more'];

/** Phone bottom tabs (PRD Part 4): Home, Move, Inventory, Purchasing, More. */
export const TABS: { key: string; to: string; label: string; icon: IconName; can?: Permission }[] = [
  { key: 'home', to: '/', label: 'Home', icon: 'home' },
  { key: 'move', to: '/stock/move', label: 'Move', icon: 'move', can: 'stock_move' },
  { key: 'inventory', to: '/stock', label: 'Inventory', icon: 'stock' },
  { key: 'purchasing', to: '/reorder', label: 'Purchasing', icon: 'purchasing' },
  { key: 'more', to: '/more', label: 'More', icon: 'more' },
];

/** Purchasing sub-navigation on phone (segmented control). */
export const PURCHASING_SEGMENTS = [
  { to: '/reorder', label: 'Reorder' },
  { to: '/purchase-requests', label: 'PRs' },
  { to: '/purchase-orders', label: 'POs' },
  { to: '/suppliers', label: 'Suppliers' },
];

const under = (path: string, base: string) => (base === '/' ? path === '/' : path === base || path.startsWith(`${base}/`));

export interface Place {
  /** Sidebar group ('' for Home, 'Settings' for pages behind the gear, null when the page is not in the menus). */
  group: string | null;
  item: NavItem | SettingsEntry | null;
  /** The page is below its menu entry (a detail page), so the phone header shows a back arrow to `item.to`. */
  detail: boolean;
}

/** Which menu entry a path belongs to: the longest matching route wins (/stock/move over /stock). */
export function placeOf(path: string): Place {
  type Entry = [group: string, item: NavItem | SettingsEntry | null, base: string];
  const entries: Entry[] = [
    ...NAV.flatMap(g => g.items.flatMap(i => [i.to, ...(i.match ?? [])].map((base): Entry => [g.group, i, base]))),
    ...SETTINGS_TABS.map((t): Entry => ['Settings', t, t.to]),
    ...SETTINGS_PATHS.map((p): Entry => ['Settings', null, p]),
  ];
  let best: Entry | undefined;
  for (const e of entries) if (under(path, e[2]) && (!best || e[2].length > best[2].length)) best = e;
  if (!best) return { group: null, item: null, detail: false };
  return { group: best[0], item: best[1], detail: !!best[1] && path !== best[1].to };
}

/** The bottom tab a path lights up: Home, Move, Inventory, any Purchasing page, else More. */
export function tabOf(path: string) {
  const { group, item } = placeOf(path);
  if (group === 'Purchasing') return 'purchasing';
  if (item?.to === '/') return 'home';
  if (item?.to === '/stock/move') return 'move';
  if (item?.to === '/stock') return 'inventory';
  return 'more';
}
